import type { MatchItem } from './scoring-types';

export interface MatchRun {
  number: number;
  matches: MatchItem[];
  playedCount: number;
}

// Americano rozdaje rundy, „każdy z każdym" powtarza stawkę kółkami — ta sama
// sekcja meczów, inna nazwa jednostki.
export type RunUnit = 'runda' | 'kółko';

export function runLabel(run: MatchRun, unit: RunUnit = 'runda'): string {
  const noun = unit === 'runda' ? 'Runda' : 'Kółko';
  const missing = run.matches.length - run.playedCount;

  if (missing === 0) {
    return `${noun} ${run.number} · komplet wyników`;
  }

  return `${noun} ${run.number} · brakuje ${missing} z ${run.matches.length}`;
}

function matchupKeyOf(match: MatchItem): string {
  return [match.side1, match.side2]
    .map((side) => side.map((player) => player.memberId).sort((first, second) => first - second).join('+'))
    .sort()
    .join(' vs ');
}

// Kolejki rozpisane przed numerowaniem kółek mają rewanż zapisany jako zwykłe
// powtórzenie tej samej pary — n-te wystąpienie zestawienia to n-te kółko.
function cycleNumbers(matches: MatchItem[]): Map<number, number> | null {
  // Bez składów nie da się powiedzieć, czy to rewanż, czy dwa różne mecze.
  if (matches.some((match) => match.side1.length === 0 || match.side2.length === 0)) {
    return null;
  }

  const played = new Map<string, number>();
  const cycleOf = new Map<number, number>();

  for (const match of matches) {
    const key = matchupKeyOf(match);
    const cycle = (played.get(key) ?? 0) + 1;

    played.set(key, cycle);
    cycleOf.set(match.id, cycle);
  }

  return Array.from(played.values()).some((cycle) => cycle > 1) ? cycleOf : null;
}

// Przy korcie liczy się runda, która właśnie się toczy — ta idzie na górę. Kółka
// „każdy z każdym" czyta się od pierwszego, więc zostają po staremu.
export function orderedRuns(runs: MatchRun[] | null, unit: RunUnit): MatchRun[] | null {
  if (runs === null || unit !== 'runda') {
    return runs;
  }

  return [...runs].reverse();
}

// Kolejka rotacyjna dokłada rundy w trakcie gry, więc płaska lista meczów kłamie:
// świeżo rozdana runda wygląda w niej jak zapomniane wyniki. Null = zwykła
// kolejka, rozpisana raz, bez rund i bez rewanżów.
export function runsOfMatches(matches: MatchItem[]): MatchRun[] | null {
  const cycleOf = matches.some((match) => typeof match.runNumber === 'number')
    ? null
    : cycleNumbers(matches);

  if (cycleOf === null && !matches.some((match) => typeof match.runNumber === 'number')) {
    return null;
  }

  const byNumber = new Map<number, MatchItem[]>();
  for (const match of matches) {
    const number = cycleOf === null ? match.runNumber ?? 0 : cycleOf.get(match.id) ?? 0;
    byNumber.set(number, [...(byNumber.get(number) ?? []), match]);
  }

  return Array.from(byNumber.entries())
    .sort(([first], [second]) => first - second)
    .map(([number, matchesInRun]) => ({
      number,
      matches: matchesInRun,
      playedCount: matchesInRun.filter((match) => match.winnerSide !== null).length,
    }));
}
